import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const roquebruneCapMartinEn: ChauffeurPage = {
  slug: "roquebrune-cap-martin",
  title: "VTC & private chauffeur in Roquebrune-Cap-Martin",
  subtitle:
    "Private chauffeur in Roquebrune-Cap-Martin: Nice airport transfers, Monaco and Menton runs, and chauffeur hire between the medieval village and Cap-Martin, 7 days a week.",
  keywords: [
    "vtc roquebrune-cap-martin",
    "private chauffeur roquebrune-cap-martin",
    "cap-martin chauffeur",
    "roquebrune nice airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Roquebrune-Cap-Martin",
    subtitle:
      "Between Monaco and Menton, from the perched medieval village to the Cap-Martin villas: Nice airport transfers (~40 min) and chauffeur hire, 24/7.",
    keywords: [
      "VTC Roquebrune-Cap-Martin",
      "Cap-Martin chauffeur",
      "Nice airport transfer",
      "Monaco & Menton runs",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "Private chauffeur & VTC in Roquebrune-Cap-Martin, between Monaco and Menton",
    badges: [
      "Nice airport ~40 min",
      "Monaco & Menton runs",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Roquebrune-Cap-Martin",
    items: [
      {
        title: "Nice airport in ~40 min",
        description:
          "A direct link to Nice Côte d'Azur airport via the A8 or the Lower Corniche, flight tracking and meet-and-greet included.",
      },
      {
        title: "A perched village, no parking worry",
        description:
          "The stepped lanes around the medieval castle are car-free: drop-off as close as possible, pick-up on a single call.",
      },
      {
        title: "At the gates of Monaco and Menton",
        description:
          "Monte-Carlo minutes away, Menton just past the border: evenings, casinos and meetings served with no driving.",
      },
      {
        title: "Discretion on Cap-Martin",
        description:
          "Villas and estates on the peninsula: a punctual, discreet chauffeur, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Roquebrune-Cap-Martin",
    services: [
      {
        title: "Nice airport transfers",
        text: "Roquebrune-Cap-Martin ⇄ Nice Côte d'Azur airport in ~40 min, with flight tracking and a personal welcome.",
      },
      {
        title: "Monaco & Menton runs",
        text: "Short journeys to Monte-Carlo, the Port of Monaco or central Menton, one-way or with waiting on site.",
      },
      {
        title: "Chauffeur hire",
        text: "By the hour or by the day, between the medieval village, Cap-Martin and the Riviera, with a dedicated chauffeur.",
      },
      {
        title: "Culture & shoreline",
        text: "Le Corbusier's Cabanon, the E-1027 villa and the coastal path: drop-off and pick-up at the headland accesses.",
      },
      {
        title: "Long distance & station",
        text: "Links to neighbouring Italy (Ventimiglia, San Remo) and transfers to Roquebrune-Cap-Martin or Monaco-Monte-Carlo station.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Medieval village",
        description:
          "Roquebrune castle and stepped lanes, drop-off at the foot of the promontory.",
      },
      {
        name: "Cap-Martin",
        description: "Residential peninsula, villas and pines facing the sea.",
      },
      {
        name: "Le Corbusier's Cabanon & villa E-1027",
        description: "Listed site along the coastal path.",
      },
      {
        name: "Roquebrune-Cap-Martin station",
        description: "Stop on the Marseille–Ventimiglia coastal line.",
      },
      {
        name: "Monaco border",
        description: "Access to Monte-Carlo and the port within minutes.",
      },
      {
        name: "Nice airport",
        description: "Terminals 1 and 2, ~40 min via the A8.",
      },
    ],
  },
  generic: {
    heading: "A chauffeur between the medieval village and Cap-Martin",
    subheading:
      "Two faces of Roquebrune-Cap-Martin: the medieval rock above, the chic peninsula below.",
    paragraphs: [
      "Roquebrune-Cap-Martin stretches from its perched medieval village — crowned by the oldest castle keep in France — down to the wooded Cap-Martin peninsula and its Belle Époque villas. KS Rent Services links these two worlds and serves the whole eastern Riviera with a private chauffeur, with no parking constraint in the stepped lanes.",
      "Wedged between Monaco and Menton, the town is ideally placed: Monte-Carlo, its casino and port are minutes away, Menton and the Italian border just beyond. We handle your transfers to Nice Côte d'Azur airport in around forty minutes, with flight tracking and a personal welcome.",
      "For a visit to Le Corbusier's Cabanon and the E-1027 villa, a walk along the coastal path or dinner in Monaco, the chauffeur drops you as close as possible and returns on a single call — you enjoy Cap-Martin without ever hunting for a space.",
    ],
    bullets: [
      "Nice airport in ~40 min",
      "Drop-off at the village foot",
      "Monaco & Menton minutes away",
    ],
  },
  process: {
    heading: "Book a chauffeur in Roquebrune-Cap-Martin",
    steps: [
      "Tell us your route (village, Cap-Martin, Monaco, Menton, airport) and your times.",
      "We confirm the right vehicle and the fixed package, or an immediate quote.",
      "The chauffeur tracks your flight and greets you at Nice airport or the agreed address.",
      "Drop-off as close as possible to the promontory and pick-up on a single call.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Roquebrune-Cap-Martin"),
    {
      q: "How long does it take to reach Nice airport?",
      a: "Around 40 minutes via the A8 or the Lower Corniche from Roquebrune-Cap-Martin, with flight tracking and a welcome on arrival. The journey stays moderate, as the town lies east of Nice.",
    },
    {
      q: "Do you serve Monaco and Menton?",
      a: "Yes. Monte-Carlo and its port are minutes away, Menton just beyond; we cover both one-way runs and waiting on site for an evening or a meeting.",
    },
    {
      q: "Can you drive up to the medieval village?",
      a: "The stepped lanes around the castle are pedestrian. The chauffeur drops you as close as possible to the village entrance and collects you at the same spot after your visit.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during major Monaco events; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to the international clientele of Cap-Martin and Monaco.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes E-Class and S-Class saloons, vans, SUVs and VIP minibuses, depending on the number of passengers and the occasion.",
    },
  ],
  relatedLinks: [
    { label: "Private chauffeur in Monaco", href: "/en/chauffeur/monaco" },
    { label: "Nice chauffeur rates", href: "/en/chauffeur/nice" },
    {
      label: "Transfers across the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Car rental with delivery in Monaco",
      href: "/en/location/car-rental-monaco",
    },
  ],
  cta: {
    title: "Your private chauffeur in Roquebrune-Cap-Martin",
    text: "Nice airport transfer, Monaco-Menton runs or chauffeur hire: fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Roquebrune-Cap-Martin — private chauffeur",
    description:
      "Private car service in Roquebrune-Cap-Martin: Nice airport transfers (~40 min), Monaco and Menton runs, from the medieval village to Cap-Martin.",
    canonical: "/en/chauffeur/roquebrune-cap-martin",
  },
};

export default roquebruneCapMartinEn;
